import { paletteFor } from "./teamColors";
import { hexToHsl, hslToHex } from "./colorMath";

// --- Branding ---

/** Brand gradient — used for active pill / nav highlights. */
export const BRAND_GRADIENT = "linear-gradient(135deg,#00A0E8 0%,#004078 90%)";

/**
 * XBG full lockup (with the "X Benefit Group" tagline) — the larger stacked
 * co-brand mark used only in the login hero's vertical orientation.
 */
export const PARENT_LOGO = "/logos/xbg-full.png";

/**
 * XBG wordmark only — the "XBG" mark with the tagline cropped off. Used as the
 * parent co-brand everywhere else (sidebar / top nav / login cap), where the
 * full stacked lockup would render smaller and higher than a single-line
 * tenant logo of the same height. Whether the *current* tenant is XBG itself
 * (and so should not repeat this mark beside its own logo) is decided by
 * tenant identity in TenantWordmark, not by comparing against this path.
 */
export const PARENT_MARK = "/logos/xbg.png";

/**
 * Dark-theme cuts of the supplied brand artwork.
 *
 * A logo is a PNG somebody sent us, so it cannot be recoloured per theme the
 * way the rest of the chrome can — and both marks the app ships are dark ink,
 * which on the dark header is ink on ink. Opacity was the old answer and it
 * only ever traded invisible for faint.
 *
 * The fix is a second asset per mark, and a MAP rather than a "-white" filename
 * convention: a tenant logo comes out of `tenants.logo`, so a convention would
 * have every tenant without a dark cut firing a 404 on every page load. An
 * entry exists only where the artwork does.
 *
 * - XBG's two marks are re-cut locally: the navy ink (and its antialiased
 *   fringe) becomes white, the cyan glyph is a brand colour and is untouched.
 * - Everon's is the official white lockup — the violet mark with a white
 *   wordmark — not a processed copy of the dark one.
 *
 * Consumers render BOTH images and let `dark:` decide (see TenantWordmark), so
 * light mode is byte-for-byte the artwork it always was and there is no
 * theme-resolution flash on first paint.
 */
export const DARK_LOGO_VARIANTS: Record<string, string> = {
  "/logos/xbg.png": "/logos/xbg-white.png",
  "/logos/xbg-full.png": "/logos/xbg-full-white.png",
  "/logos/everon.png": "/logos/everon-white.png",
};

/** The dark-theme cut of `src`, or null when the artwork has no dark variant. */
export const darkLogoFor = (src: string | null | undefined): string | null => (src && DARK_LOGO_VARIANTS[src]) || null;

/** Everon platform mark (the app's favicon), used in the "Powered by Everon" credit. */
export const EVERON_LOGO = "/favicon.png";

/**
 * The product name, hardcoded — never stored. `tenants.name` holds only the
 * company name (e.g. "XBG"); this is appended wherever the tenant's full
 * branded name is shown (e.g. "XBG Backlog"). Not used for the default/
 * "Welcome" tenant, whose name is already a complete label on its own —
 * TenantWordmark's no-logo fallback renders that name as-is.
 */
export const APP_NAME = "Backlog";

// --- Timing / threshold constants ---

/** Delay before showing loading retry hint (ms) */
export const LOADING_HINT_DELAY_MS = 6000;

/** Delay after saving before resetting the saving ref, to let queries settle (ms) */
export const SAVE_SETTLE_DELAY_MS = 500;

/** Maximum number of attachments per backlog item */
export const MAX_ATTACHMENTS = 10;

/** Minimum password length — must match MIN_PASSWORD_LENGTH in api/lib/authRoutes.ts, which enforces it. */
export const MIN_PASSWORD_LENGTH = 10;

/** @deprecated Use MAX_ATTACHMENTS instead */
export const MAX_SCREENSHOTS = MAX_ATTACHMENTS;

// Centralized constants for database IDs used across the application.
// These correspond to rows in the Supabase database and must stay in sync.
//
// Status semantics no longer live here as hardcoded UUIDs — a status carries
// its own behaviour in dropdown_options.kind, resolved through
// src/lib/statusKind.ts, so a tenant can define its own status set.

// --- Subsheet IDs ---

export const AFFILIATE_PORTAL_ID = "4d0dc343-3436-494f-a956-61fe626185b6";
export const ADMIN_PORTAL_ID = "264a6274-b7a7-4482-afb2-edc7323a0a6b";

/** Sentinel value for the "All Items" view (admin only, no subsheet filter) */
export const ALL_ITEMS_ID = "__all__";

// There was an `UNASSIGNED_FILTER_ID` sentinel here — a "no assignee" value in
// the Assigned column filter, which had itself replaced a whole /unassigned
// page. Both are gone: an unassigned ticket is not a state this workspace has
// (a ticket is assigned to someone when it is filed), so the filter offered a
// facet that never matches and the page listed nothing. Grouping by assignee
// still keeps its `__none__` bucket for legacy rows that predate the rule —
// what went is every control that invites you to go looking for them.

// Dropdown container ids are resolved by name ("Status", "Priority",
// "Ticket Type") at read time, not hardcoded — each tenant has its own sets.

// --- Priority IDs ---

export const CRITICAL_PRIORITY_ID = "bbb00001-0000-4000-a000-000000000001";
export const HIGH_PRIORITY_ID = "bbb00002-0000-4000-a000-000000000002";
export const MEDIUM_PRIORITY_ID = "bbb00003-0000-4000-a000-000000000003";
export const LOW_PRIORITY_ID = "bbb00004-0000-4000-a000-000000000004";

// --- Dropdown Colors ---

/** Override text color for specific dropdown items instead of using textColorForBg(). */
export const DROPDOWN_TEXT_COLORS: Record<string, string> = {};

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * Resolve each dropdown option's chip colour.
 *
 * The colour lives on the row now (dropdown_options.color), not in a build-time
 * map, so per-tenant statuses render. A row with no usable stored colour falls
 * back to the deterministic palette hash shared with team chips, so it still
 * renders a stable colour rather than a flat grey.
 */
export const applyDropdownColors = <T extends { id: string; color?: string | null }>(
  items: T[],
): (T & { color: string })[] =>
  items.map((item) => ({ ...item, color: item.color && HEX.test(item.color) ? item.color : paletteFor(item.id) }));

/** Perceived brightness of a hex colour, 0-255 (the W3C weighted average). */
const perceivedBrightness = (hex: string): number => {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
};

/** Return a readable text color for a given hex background. Checks DROPDOWN_TEXT_COLORS first when an id is provided. */
export const textColorForBg = (hex: string, id?: string): string => {
  if (id && DROPDOWN_TEXT_COLORS[id]) return DROPDOWN_TEXT_COLORS[id];
  // W3C relative luminance threshold
  return perceivedBrightness(hex) < 150 ? "#fff" : "#000";
};

/** Return a darker shade of a hex color for badge borders (25% darker). */
export const borderColorForBg = (hex: string): string => {
  const c = hex.replace("#", "");
  const r = Math.round(parseInt(c.substring(0, 2), 16) * 0.75);
  const g = Math.round(parseInt(c.substring(2, 4), 16) * 0.75);
  const b = Math.round(parseInt(c.substring(4, 6), 16) * 0.75);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

/** Blend a hex colour toward white by `ratio` (0-1). */
const mixWithWhite = (hex: string, ratio: number): string => {
  const c = hex.replace("#", "");
  const mix = (start: number) => Math.round(start + (255 - start) * ratio);
  const r = mix(parseInt(c.substring(0, 2), 16));
  const g = mix(parseInt(c.substring(2, 4), 16));
  const b = mix(parseInt(c.substring(4, 6), 16));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

/**
 * Brightness below which a dropdown colour is treated as "heavy" and softened
 * for the priority flag. The pastel palette the flags were designed around
 * (#ffcfc9 High, #FFE5A0 Medium, #bfe1f6 Low) all sit above 200; Critical's
 * stored #b10202 lands at 54.
 */
const HEAVY_COLOR_BRIGHTNESS = 140;

/**
 * Pull a heavy stored colour into the pastel band the dropdown palette was
 * designed around; pastels pass through untouched. Hue and ordering survive the
 * blend, so a softened colour is still recognisably itself — one register
 * quieter, not a different colour.
 */
const intoPastelBand = (hex: string): string =>
  perceivedBrightness(hex) < HEAVY_COLOR_BRIGHTNESS ? mixWithWhite(hex, 0.45) : hex;

/**
 * How much more saturated a stored colour is drawn in dark mode. A tenth.
 *
 * A pastel loses a little of its chroma against a dark ground — the eye reads
 * the same colour as flatter with less light behind it — and a tenth is the
 * step that gives it back. It is deliberately small: an earlier pass forced
 * these colours to a fixed high saturation and a fixed mid lightness, which
 * did make High and Low unmistakable and also made the priority column look
 * like a set of warning lights. The palette is pastel because the ranks are
 * meant to whisper.
 */
const DARK_SATURATION_LIFT = 1.1;

/**
 * The dark-mode rendering of a colour the light theme already renders: the
 * SAME colour, a tenth more saturated.
 *
 * Hue and lightness are untouched, so nothing about the mark's weight, its
 * ordering against its neighbours, or its relationship to the fill it outlines
 * changes — only how much of itself the colour keeps on a dark surface. Applied
 * to a fill and its outline alike, so the pair stays the pair.
 *
 * Near-greys pass through: a colourless tag has no chroma to restore, and a
 * tenth more of nothing is nothing.
 */
export const saturateForDark = (hex: string): string => {
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;
  return hslToHex({ ...hsl, s: Math.min(100, hsl.s * DARK_SATURATION_LIFT) });
};

/**
 * Hue band treated as "the red end" of a priority palette, and how far a
 * colour in it is taken out of the white.
 *
 * A priority ladder crowds at the top: the ranks that matter most are the ones
 * a reader has to tell apart fastest, and in the shipped palette they were
 * Medium's pale yellow (#FFE5A0, hue 43°) against High's pale pink (#ffcfc9,
 * hue 7°) — two colours of the same near-white lightness, which at 18px is
 * most of what the eye has to go on. High is already at full saturation for
 * its hue, so there is no chroma left to add; what separates it is LIGHTNESS,
 * and a tenth out of the white is the step that does it without leaving the
 * pastel register the palette is built in.
 *
 * Applied by hue rather than by rank, because rank is not something a colour
 * knows: a tenant defines its own priorities, and the red end of whatever
 * palette it picks is the end that has to separate. Yellow, green and blue
 * ranks pass through untouched, so the ladder gains contrast exactly where it
 * was crowded.
 */
const RED_BAND_DEGREES = 20;
const RED_DEEPEN = 0.9;

/** A tenth deeper, for a colour at the red end of the wheel; others pass. */
const deepenRed = (hex: string): string => {
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;
  const inBand = hsl.h <= RED_BAND_DEGREES || hsl.h >= 360 - RED_BAND_DEGREES;
  return inBand ? hslToHex({ ...hsl, l: hsl.l * RED_DEEPEN }) : hex;
};

/**
 * The fill colour of a priority flag.
 *
 * The dropdown palette is pastel by design — it was built for filled pills, on
 * which a saturated colour would swallow its own label. Nothing stops a tenant
 * storing a fully saturated colour anyway, and Critical ships as `#b10202`: as
 * an 18px glyph beside High's pale pink that red shouted a whole register
 * louder than one rank of difference is worth. Anything below the pastel band
 * is blended toward white until it sits in the same register as its
 * neighbours; the pastels themselves pass through untouched.
 *
 * The blend keeps hue and ordering, so Critical stays an unmistakably deeper,
 * redder flag than High — softer, not the same. `deepenRed` then takes both of
 * them one step further out of the white, which is what opens the gap between
 * High and Medium (see its own note). The shipped ladder lands at
 * #bfe1f6 Low → #ffe5a0 Medium → #ffa69b High → #cc5b5b Critical: pale blue,
 * pale yellow, coral, muted red — four steps that read as four ranks, where
 * the middle two used to read as one.
 *
 * Dark mode inherits the shift rather than repeating it: the dark pair is
 * `saturateForDark` of exactly these values (see BacklogRow), so moving the
 * light value moves both.
 */
export const flagColorFor = (hex: string): string => deepenRed(intoPastelBand(hex));

/**
 * A second, per-rank saturation bump for the priority flag's fill and
 * outline, on top of everything above.
 *
 * `deepenRed`'s tenth was the correction to an earlier pass that had forced
 * these colours to a fixed high saturation and fixed lightness (see
 * `DARK_SATURATION_LIFT`'s note) — this is a later, separate ask: High,
 * Medium and Low read a touch flat next to Critical and were asked to be
 * lifted individually (High more than Medium/Low). Same convention
 * `DARK_SATURATION_LIFT` already uses for exactly this reason — a relative
 * multiplier on the HSL S channel, clamped to 100 — kept per-rank because
 * "more saturated" was not the same ask for all three. Critical carries no
 * entry here and stays exactly what `flagColorFor` already produced.
 *
 * Keyed by the fixed per-priority ids rather than hue: a stored colour does
 * not know its own rank, and High and Critical land in the same red hue band
 * `deepenRed` reads (see `RED_BAND_DEGREES`) despite needing different
 * treatment here. These ids are the seeded rows' actual ids (0002_seed.sql,
 * 0023_dropdown_options_color_kind.sql), not a per-tenant-generated id, so
 * matching on id is stable across the workspaces seeded from them; a
 * workspace whose Priority rows were generated with fresh ids falls through
 * this map untouched, same as Critical.
 */
const FLAG_SATURATION_LIFT: Record<string, number> = {
  [HIGH_PRIORITY_ID]: 1.15,
  [MEDIUM_PRIORITY_ID]: 1.1,
  [LOW_PRIORITY_ID]: 1.1,
};

/**
 * Apply `FLAG_SATURATION_LIFT`'s per-rank bump to one flag colour.
 *
 * Called separately on the fill and on the outline (see `PriorityFlag`), so
 * each gets the lift on whatever headroom it actually has:
 *
 * The outline `borderColorForBg` derives is well under the HSL ceiling (High
 * and Medium sit in the 35-37% range), so it takes the bump the same way
 * `saturateForDark` already does elsewhere — a relative multiplier on S,
 * clamped to 100 — and visibly deepens.
 *
 * High's and Medium's stored *fill* colours, though, are pastels with a
 * channel already at 255, which pins HSL's S to exactly 100 regardless of how
 * washed out the colour looks — an S multiplier has no headroom there and
 * would be a silent no-op (this is what made the first cut of this function
 * a no-op for two of the three ranks it was asked to move). At S ≈ 100 the
 * lever HSL actually has left is lightness: `newL = L - (L - 50) * (lift -
 * 1)` pulls L a `lift - 1` fraction of the way from wherever it sits toward
 * 50 (the midpoint where a hue reads most vivid), which is what actually
 * reads as "more saturated" for a near-white pastel. Hue and S are left
 * alone. Low's fill has real S headroom (75%), so it stays on the plain
 * multiplier and is unaffected by this branch.
 *
 * Dark mode (`saturateForDark`) only ever touches S, never L, so a fill
 * lightened this way carries the same L into dark mode untouched — there is
 * no separate dark-mode darkening to guard against here.
 */
const SATURATION_CEILING_THRESHOLD = 99;

export const boostFlagSaturation = (hex: string, priorityId: string | undefined): string => {
  const lift = priorityId ? FLAG_SATURATION_LIFT[priorityId] : undefined;
  if (!lift) return hex;
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;
  if (hsl.s >= SATURATION_CEILING_THRESHOLD) {
    return hslToHex({ ...hsl, l: hsl.l - (hsl.l - 50) * (lift - 1) });
  }
  return hslToHex({ ...hsl, s: Math.min(100, hsl.s * lift) });
};

/**
 * The fill colour of a status pill in Home's personal work lists.
 *
 * The same problem `flagColorFor` solves for the priority flag, one surface
 * over. The starter status set is pastel apart from one entry — Blocked ships
 * as `#EF4444` (Rejected's `#991B1B` is darker still) — and Home's "Needs you"
 * tab is a list of exactly those: five saturated red capsules stacked down a
 * page whose whole job is to read as calm, each one beside a red "blocked 4d"
 * counter saying the same thing again.
 *
 * Softened, Blocked is still plainly the red row — hue and ordering survive the
 * blend, and the pill's own text colour is re-derived from the softened fill by
 * `textColorForBg`, so it stays legible in both themes. The backlog table is
 * left alone deliberately: there a status pill is a value in a column of
 * values, not the loudest thing on a personal dashboard.
 */
export const workPillColorFor = (hex: string): string => intoPastelBand(hex);

/**
 * Light- and dark-mode colours for ColorBadge's `tinted` variant, computed
 * together so the badge can switch with the `.dark` class alone (see
 * `.colorbadge-tinted` in index.css) instead of re-rendering on theme change.
 *
 * Light mode keeps the original recipe: a translucent tint of the stored
 * colour behind text in that same colour, tuned to read against a near-white
 * page. Against dark mode's near-black surface that same text is only ~3:1 —
 * the tint barely lifts the background. Dark mode instead raises the tint's
 * opacity and blends the text 55% toward white, which lands every colour in
 * TEAM_PALETTE between 6.9:1 and 7.9:1 against the dark background, without
 * erasing the colour's hue.
 */
export const tintedBadgeColors = (hex: string) => ({
  light: { background: `${hex}20`, text: hex, border: `${hex}40` },
  // The dark border keeps its own alpha — a translucent tint over a dark card
  // is mostly card, which is why it is heavier here than the 40 light mode
  // uses — and takes the same tenth of extra saturation every other stored
  // colour gets in dark mode, so a row of team tags reads as tags rather than
  // as a row of identical grey capsules. The fill stays the quiet tint: the
  // chip is a tint, not a badge.
  dark: { background: `${hex}33`, text: mixWithWhite(hex, 0.55), border: `${saturateForDark(hex)}59` },
});

/**
 * The custom-property pair a chart mark is painted from: the stored colour, and
 * the same colour a tenth more saturated for dark mode (`saturateForDark`).
 *
 * The same two-properties-and-a-class trick the priority flag uses (see
 * `.flag-glyph` in index.css, and `.chart-fill` / `.donut-slice` beside it):
 * these colours come from the tenant's own stored hexes, so they arrive as
 * inline styles, and an inline style cannot answer `.dark` on its own. Emitting
 * BOTH values and letting CSS choose keeps the switch on the theme class — no
 * re-render, no first-paint flash of the wrong palette.
 *
 * Why the donuts and meters needed it at all: the dropdown palette is pastel,
 * built for filled pills on a near-white page, and a 13px-wide arc of a pastel
 * on a dark card gives up exactly the chroma that told one slice from the next.
 * A tenth is what it gives back — the same tenth, and no more, so a breakdown
 * on a dark screen is the same chart rather than a brighter one.
 */
export const chartColorVars = (hex: string): Record<string, string> => ({
  "--chart-fill": hex,
  "--chart-fill-dark": saturateForDark(hex),
});

/** A hex color as an rgba() string at the given alpha (0-1), for translucent chart fills. */
export const hexToRgba = (hex: string, alpha: number): string => {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
